import { ReportData } from '../types/report.types';
import { sortFindings, severityOrder } from './reportSharedUtils';

// Export shared helper mappings directly for backward compatibility
export const normalizeSeverity = (severity: string | null | undefined): 'Critical' | 'High' | 'Medium' | 'Low' | 'Info' => {
  const s = (severity || 'Info').trim();
  if (!s || s.toLowerCase() === 'none') return 'Info';
  const normalized = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  const validSeverities = ['Critical', 'High', 'Medium', 'Low', 'Info'];
  if (validSeverities.includes(normalized)) {
    return normalized as 'Critical' | 'High' | 'Medium' | 'Low' | 'Info';
  }
  return 'Info';
};

/**
 * Checks if a program has zero findings
 * @param reportData - The report data to check
 * @returns boolean indicating if there are zero findings
 */
export const hasZeroFindings = (reportData: ReportData | null | undefined): boolean => {
  if (!reportData) return true;

  // Most reliable check: total_findings
  if (typeof reportData.total_findings === 'number') {
    const result = reportData.total_findings === 0;
    return result;
  }

  // Check detailed_findings array
  if (reportData.detailed_findings && reportData.detailed_findings.length > 0) {
    return false;
  }

  // Check severity counts
  const severityCounts = reportData.open_close_counts_by_severity;
  if (severityCounts) {
    const totalFromSeverity =
      (severityCounts.Critical?.Total || 0) +
      (severityCounts.High?.Total || 0) +
      (severityCounts.Medium?.Total || 0) +
      (severityCounts.Low?.Total || 0) +
      (severityCounts.Info?.Total || 0);

    if (totalFromSeverity > 0) {
      return false;
    }
  }

  // Additional check: if total_open or total_closed are greater than 0
  const hasOpen = (reportData.total_open || 0) > 0;
  const hasClosed = (reportData.total_closed || 0) > 0;

  if (hasOpen || hasClosed) {
    return false;
  }

  // If all checks pass, there are zero findings
  return true;
};

/**
 * Checks if all findings are closed (no open findings remain)
 */
export const hasAllFindingsClosed = (reportData: ReportData | null | undefined): boolean => {
  if (!reportData) return false;
  // Prefer explicit totals when available
  if (typeof reportData.total_open === 'number') {
    if (reportData.total_open === 0 && (reportData.total_closed || 0) > 0) return true;
  }
  // Check severity counts for Open sums
  const sc = reportData.open_close_counts_by_severity;
  if (sc) {
    const sumOpen = (sc.Critical?.Open || 0) + (sc.High?.Open || 0) + (sc.Medium?.Open || 0) + (sc.Low?.Open || 0) + (sc.Info?.Open || 0);
    const sumClosed = (sc.Critical?.Closed || 0) + (sc.High?.Closed || 0) + (sc.Medium?.Closed || 0) + (sc.Low?.Closed || 0) + (sc.Info?.Closed || 0);
    if (sumOpen === 0 && sumClosed > 0) return true;
  }
  // Fallback to detailed_findings statuses
  if (Array.isArray(reportData.detailed_findings) && reportData.detailed_findings.length > 0) {
    const anyOpen = reportData.detailed_findings.some(f => (String((f as any).status || '').trim().toLowerCase() !== 'closed'));
    return !anyOpen;
  }
  return false;
};

/**
 * Determine if a finding should be considered Closed using multiple signals
 */
export const isFindingClosed = (reportData: ReportData | null | undefined, finding: any): boolean => {
  if (!finding) return false;
  // Boolean flags take priority if present
  const boolVal = (finding.is_closed ?? finding.closed);
  if (typeof boolVal === 'boolean') return boolVal;

  const candidates: string[] = [];
  const direct = finding.status ?? finding.finding_status ?? finding.report_status;
  if (direct) candidates.push(String(direct));

  // Cross-reference with reports_list by abbreviation or title
  const abbr = String(finding.abbreviation || '').trim().toLowerCase();
  const title = String(finding.title || '').trim().toLowerCase();
  const match = (reportData?.reports_list || []).find(r => {
    const ra = String(r.abbreviation || '').trim().toLowerCase();
    const rt = String(r.title || '').trim().toLowerCase();
    return (abbr && ra === abbr) || (title && rt === title);
  });
  if (match?.status) candidates.push(String(match.status));

  const normalized = candidates.map(s => s.trim().toLowerCase()).filter(Boolean);
  
  // Explicitly check for closed keywords
  if (normalized.some(s => ['closed', 'fixed', 'remediated', 'resolved'].includes(s))) return true;

  return false;
};

/**
 * Determines if a finding is "Open" or should be shown by default.
 */
export const isFindingActuallyOpen = (reportData: ReportData | null | undefined, finding: any): boolean => {
  if (!finding) return false;
  
  // 1. If it's explicitly closed, it's not open
  if (isFindingClosed(reportData, finding)) return false;

  // 2. Check the status string for Risk Accepted or Non-Actionable
  const s = String(finding.status || finding.finding_status || '').toLowerCase().trim();
  
  // Exclude Risk Accepted and Non-Actionable from the default "Open" set if explicitly marked so
  if (s.includes('accept') || s.includes('risk')) return false;
  if (s.includes('non-actionable') || s.includes('non actionable')) return false;

  // Otherwise, if it wasn't closed, we treat it as Open
  return true;
};

/**
 * Generates a stable, unique key for a finding.
 */
export const getFindingStableKey = (finding: any, index?: number): string => {
  if (!finding) return 'unknown';
  
  // 1. Primary choice: report_id or id
  if (finding.report_id) return `r-${finding.report_id}`;
  if (finding.id) return `i-${finding.id}`;
  
  // 2. Secondary choice: Title or Abbreviation
  const title = String(finding.title || '').trim().toLowerCase();
  const abbr = String(finding.abbreviation || '').trim().toLowerCase();
  if (title) return `t-${title}`;
  if (abbr) return `a-${abbr}`;
  
  const idx = index !== undefined ? `-${index}` : '';
  return `f-idx${idx}`;
};

/**
 * Gets the appropriate preview mode based on findings count
 */
export const getAppropriatePreviewMode = (
  reportData: ReportData | null | undefined,
  currentMode?: string
): 'executive' | 'technical' | 'full' => {
  if (hasZeroFindings(reportData)) {
    return 'executive';
  }
  if (hasAllFindingsClosed(reportData)) {
    return 'technical';
  }
  return 'full';
};

/**
 * Gets available preview modes based on findings count
 */
export const getAvailablePreviewModes = (reportData: ReportData | null | undefined): string[] => {
  if (hasZeroFindings(reportData)) {
    return ['executive'];
  }

  const config = reportData?.finding_config;
  if (config && config.individualFindings) {
    const anyExplicitlyIncluded = Object.values(config.individualFindings).some((inf: any) => inf.included === true);
    if (anyExplicitlyIncluded) {
      return ['full', 'technical'];
    }
  }

  if (hasAllFindingsClosed(reportData)) {
    return ['technical'];
  }
  return ['full', 'technical'];
};

/**
 * Checks if a specific preview mode is available for the given report data
 */
export const isPreviewModeAvailable = (
  reportData: ReportData | null | undefined,
  mode: string
): boolean => {
  const availableModes = getAvailablePreviewModes(reportData);
  return availableModes.includes(mode);
};

const getEffectiveStatus = (s1?: string, s2?: string): string => {
  const str1 = String(s1 || '').trim();
  const str2 = String(s2 || '').trim();

  const isNonOpen = (s: string) => {
    const l = s.toLowerCase();
    return Boolean(l && l !== 'open');
  };

  if (isNonOpen(str1)) return str1;
  if (isNonOpen(str2)) return str2;
  return str1 || str2 || 'Open';
};

/**
 * Calculates severity counts from findings, robustly handling blank/None severities as Info
 */
export const calculateSeverityCounts = (reportData: ReportData | null | undefined) => {
  const counts = {
    Critical: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
    High: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
    Medium: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
    Low: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
    Info: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 }
  };

  if (!reportData) return counts;

  // Use Map keying on unique finding key (report_id/id/title) to avoid title collision counts
  const uniqueFindingsMap = new Map<string, any>();
  const addFindingToMap = (f: any, idx: number) => {
    if (!f) return;
    const key = getFindingStableKey(f, idx);
    if (!uniqueFindingsMap.has(key)) {
      uniqueFindingsMap.set(key, { ...f });
    } else {
      const existing = uniqueFindingsMap.get(key);
      uniqueFindingsMap.set(key, {
        ...existing,
        ...f,
        status: getEffectiveStatus(existing.status, f.status),
        severity_category: f.severity_category || existing.severity_category,
        abbreviation: f.abbreviation || existing.abbreviation,
      });
    }
  };

  (reportData.reports_list || []).forEach((f, idx) => addFindingToMap(f, idx));
  (reportData.detailed_findings || []).forEach((f, idx) => addFindingToMap(f, idx));

  uniqueFindingsMap.forEach((f) => {
    let severity = (f.severity_category || 'Info').trim();
    if (!severity || severity.toLowerCase() === 'none') severity = 'Info';

    const normalizedSeverity = severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase() as keyof typeof counts;

    if (counts[normalizedSeverity]) {
      counts[normalizedSeverity].Total++;

      const status = (f.status || '').toLowerCase().trim();
      
      const isClosed = ['closed', 'fixed', 'remediated', 'resolved'].includes(status);
      const isBAR = ['business accepts risk', 'business accept risk', 'risk accepted'].includes(status);
      const isBNA = [
        'non-actionable', 
        'business non-actionable', 
        'non actionable', 
        'business non actionable', 
        'business non actionable issue'
      ].includes(status);

      if (isClosed) {
        counts[normalizedSeverity].Closed++;
      } else if (isBAR) {
        counts[normalizedSeverity].BusinessAcceptsRisk++;
      } else if (isBNA) {
        counts[normalizedSeverity].BusinessNonActionable++;
      } else {
        counts[counts[normalizedSeverity] ? normalizedSeverity : 'Info'].Open++;
      }
    }
  });

  return counts;
};

export const SEVERITY_ORDER = ['Critical', 'High', 'Medium', 'Low', 'Info'];

/**
 * Generates a global map of finding titles to abbreviations (e.g., C1, I1).
 */
export const getGlobalAbbreviations = (reportData: ReportData | null | undefined): Record<string, string> => {
  const abbrMap: Record<string, string> = {};
  if (!reportData) return abbrMap;

  const allFindings = [
    ...(reportData.reports_list || []),
    ...(reportData.detailed_findings || [])
  ];

  const uniqueFindings: any[] = [];
  const seenKeys = new Set<string>();

  allFindings.forEach((f, idx) => {
    if (!f) return;
    const key = getFindingStableKey(f, idx);
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      uniqueFindings.push({ ...f });
    } else {
      const existingIdx = uniqueFindings.findIndex((uf, ufIdx) => getFindingStableKey(uf, ufIdx) === key);
      if (existingIdx !== -1) {
        uniqueFindings[existingIdx] = {
          ...uniqueFindings[existingIdx],
          ...f,
          abbreviation: f.abbreviation || uniqueFindings[existingIdx].abbreviation,
          severity_category: f.severity_category || uniqueFindings[existingIdx].severity_category,
        };
      }
    }
  });

  const sortedFindings = sortFindings(uniqueFindings);

  const counts: Record<string, number> = {
    Critical: 0,
    High: 0,
    Medium: 0,
    Low: 0,
    Info: 0
  };

  const prefixes: Record<string, string> = {
    Critical: 'C',
    High: 'H',
    Medium: 'M',
    Low: 'L',
    Info: 'I'
  };

  sortedFindings.forEach(finding => {
    const sev = normalizeSeverity(finding.severity_category);
    counts[sev]++;
    const titleKey = (finding.title || '').toLowerCase().trim();
    const explicitAbbr = (finding.abbreviation || '').trim();
    if (titleKey) {
      abbrMap[titleKey] = explicitAbbr || `${prefixes[sev]}${counts[sev]}`;
    }
  });

  return abbrMap;
};

/**
 * Processes a string of attachment URLs into PDF elements.
 */
export const processAttachmentsToElements = (attachments: string): any[] => {
  if (!attachments) return [];
  const urls = attachments.split(/[;,]/).map(u => u.trim()).filter(u => u.length > 0);
  return urls.map(url => ({
    type: 'image',
    src: url,
    width: 400
  }));
};

/**
 * Filters ReportData findings and reports by run_id if run_id is present, and recalculates counts.
 */
export const filterReportDataByRunId = (reportData: ReportData | undefined): ReportData | undefined => {
  if (!reportData) return undefined;

  // If it's a scoped/combined report, the backend has already strictly filtered the findings by runs/assets/targets/dates.
  // We skip single-run ID filtering to display all selected findings.
  const hasMultipleRuns = reportData.run_ids && Array.isArray(reportData.run_ids) && reportData.run_ids.length > 0;
  const hasCustomDateRange = reportData.start_date && reportData.end_date;
  if (hasMultipleRuns || hasCustomDateRange) {
    return reportData;
  }

  const runId = reportData.run_id;
  if (runId === undefined || runId === null || runId === '') {
    return reportData;
  }

  const targetRunId = Number(runId);

  const hasAnyRunId = (reportData.detailed_findings || []).some(f => {
    const fRunId = f.run_id !== undefined ? f.run_id : f.runId;
    return fRunId !== undefined && fRunId !== null;
  });

  const filteredFindings = !hasAnyRunId 
    ? (reportData.detailed_findings || [])
    : (reportData.detailed_findings || []).filter(f => {
        const fRunId = f.run_id !== undefined ? f.run_id : f.runId;
        return fRunId === undefined || fRunId === null || fRunId === '' || Number(fRunId) === targetRunId;
      });

  const filteredReportIds = new Set(filteredFindings.map(f => f.report_id).filter(Boolean));
  const filteredTitles = new Set(filteredFindings.map(f => String(f.title).toLowerCase().trim()));

  const filteredReportsList = (reportData.reports_list || []).filter(r => {
    const rRunId = (r as any).run_id !== undefined ? (r as any).run_id : (r as any).runId;
    if (rRunId !== undefined && rRunId !== null && rRunId !== '') {
      return Number(rRunId) === targetRunId;
    }
    return true;
  });

  const newSeverityCounts: Record<string, number> = {
    Critical: 0,
    High: 0,
    Medium: 0,
    Low: 0,
    Info: 0,
    None: 0,
    Total: 0
  };

  const newOpenCloseCounts = {
    Critical: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
    High: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
    Medium: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
    Low: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 },
    Info: { Open: 0, Closed: 0, BusinessAcceptsRisk: 0, BusinessNonActionable: 0, Total: 0 }
  };

  let totalOpen = 0;
  let totalClosed = 0;
  let totalBAR = 0;
  let totalBNA = 0;
  let totalFindings = 0;

  const uniqueFindingsMap = new Map<string, any>();
  const addFindingToMap = (f: any, idx: number) => {
    if (!f) return;
    const key = getFindingStableKey(f, idx);
    if (!uniqueFindingsMap.has(key)) {
      uniqueFindingsMap.set(key, { ...f });
    } else {
      const existing = uniqueFindingsMap.get(key);
      uniqueFindingsMap.set(key, {
        ...existing,
        ...f,
        status: getEffectiveStatus(existing.status, f.status),
        severity_category: f.severity_category || existing.severity_category,
        abbreviation: f.abbreviation || existing.abbreviation,
      });
    }
  };

  filteredReportsList.forEach((f, idx) => addFindingToMap(f, idx));
  filteredFindings.forEach((f, idx) => addFindingToMap(f, idx));

  uniqueFindingsMap.forEach((f) => {
    let severity = (f.severity_category || 'Info').trim();
    if (!severity || severity.toLowerCase() === 'none') severity = 'Info';
    const normalizedSeverity = severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase();

    if (normalizedSeverity in newOpenCloseCounts) {
      const sevKey = normalizedSeverity as keyof typeof newOpenCloseCounts;
      newOpenCloseCounts[sevKey].Total++;
      newSeverityCounts[normalizedSeverity] = (newSeverityCounts[normalizedSeverity] || 0) + 1;
      newSeverityCounts.Total++;
      totalFindings++;

      const status = (f.status || '').toLowerCase().trim();
      const isClosed = ['closed', 'fixed', 'remediated', 'resolved'].includes(status);
      const isBAR = ['business accepts risk', 'business accept risk', 'risk accepted'].includes(status);
      const isBNA = [
        'non-actionable', 
        'business non-actionable', 
        'non actionable', 
        'business non actionable', 
        'business non actionable issue'
      ].includes(status);

      if (isClosed) {
        newOpenCloseCounts[sevKey].Closed++;
        totalClosed++;
      } else if (isBAR) {
        newOpenCloseCounts[sevKey].BusinessAcceptsRisk++;
        totalBAR++;
      } else if (isBNA) {
        newOpenCloseCounts[sevKey].BusinessNonActionable++;
        totalBNA++;
      } else {
        newOpenCloseCounts[sevKey].Open++;
        totalOpen++;
      }
    } else {
      newSeverityCounts.None = (newSeverityCounts.None || 0) + 1;
      newSeverityCounts.Total++;
      totalFindings++;
    }
  });

  return {
    ...reportData,
    detailed_findings: filteredFindings,
    reports_list: filteredReportsList,
    severity_counts: newSeverityCounts,
    open_close_counts_by_severity: newOpenCloseCounts,
    total_findings: totalFindings,
    total_open: totalOpen,
    total_closed: totalClosed,
    total_business_accepts_risk: totalBAR,
    total_business_non_actionable: totalBNA,
  };
};
